export const CALENDAR_EVENT_COLORS = {
  ACTIVE: "#0EA5A4",
  COMPLETED: "#94A3B8",
  CANCELLED: "#DC2626",
  SCHEDULED: "#2563EB",
  UPCOMING: "#2563EB",
  PAST: "#94A3B8",
} as const;

export const ATTENDANCE_STATUS_BADGE_CLASS = {
  unmarked:
    "bg-slate-100 text-slate-700 dark:bg-slate-900/25 dark:text-slate-200",
  checked_in:
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300",
  checked_out: "bg-sky-100 text-sky-700 dark:bg-sky-900/20 dark:text-sky-300",
  absent: "bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300",
  excused_absence:
    "bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300",
  unknown:
    "bg-slate-100 text-slate-700 dark:bg-slate-900/25 dark:text-slate-200",
} as const;

export const ATTENDANCE_FILTER_ACTIVE_CLASS = {
  unmarked: "border-slate-600 bg-slate-600 text-white shadow-sm",
  checked_in: "border-emerald-600 bg-emerald-600 text-white shadow-sm",
  checked_out: "border-sky-600 bg-sky-600 text-white shadow-sm",
  absent: "border-rose-600 bg-rose-600 text-white shadow-sm",
  excused_absence: "border-amber-500 bg-amber-500 text-white shadow-sm",
} as const;

export const ATTENDANCE_FILTER_INACTIVE_CLASS = {
  unmarked:
    "border-slate-300 bg-slate-100 text-slate-700 hover:bg-slate-200 dark:border-slate-500/40 dark:bg-slate-500/20 dark:text-slate-200 dark:hover:bg-slate-500/30",
  checked_in:
    "border-emerald-300 bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:border-emerald-500/40 dark:bg-emerald-500/20 dark:text-emerald-200 dark:hover:bg-emerald-500/30",
  checked_out:
    "border-sky-300 bg-sky-100 text-sky-700 hover:bg-sky-200 dark:border-sky-500/40 dark:bg-sky-500/20 dark:text-sky-200 dark:hover:bg-sky-500/30",
  absent:
    "border-rose-300 bg-rose-100 text-rose-700 hover:bg-rose-200 dark:border-rose-500/40 dark:bg-rose-500/20 dark:text-rose-200 dark:hover:bg-rose-500/30",
  excused_absence:
    "border-amber-300 bg-amber-100 text-amber-700 hover:bg-amber-200 dark:border-amber-500/40 dark:bg-amber-500/20 dark:text-amber-200 dark:hover:bg-amber-500/30",
  unknown:
    "border-slate-300 bg-slate-100 text-slate-700 hover:bg-slate-200 dark:border-slate-500/40 dark:bg-slate-500/20 dark:text-slate-200 dark:hover:bg-slate-500/30",
} as const;

export const getAttendanceFilterButtonClass = (
  value: string,
  active: boolean
): string => {
  const normalized = value.toLowerCase();
  const key =
    normalized === "unmarked" ||
    normalized === "checked_in" ||
    normalized === "checked_out" ||
    normalized === "absent" ||
    normalized === "excused_absence"
      ? normalized
      : "unknown";

  if (!active) {
    return ATTENDANCE_FILTER_INACTIVE_CLASS[
      key as keyof typeof ATTENDANCE_FILTER_INACTIVE_CLASS
    ];
  }

  return ATTENDANCE_FILTER_ACTIVE_CLASS[
    (key === "unknown"
      ? "unmarked"
      : key) as keyof typeof ATTENDANCE_FILTER_ACTIVE_CLASS
  ];
};

export const SESSION_PARTICIPANT_ROLE_BADGE_CLASS = {
  manager:
    "bg-orange-100 text-orange-700 dark:bg-orange-900/25 dark:text-orange-200",
  trainer: "bg-teal-100 text-teal-700 dark:bg-teal-900/25 dark:text-teal-200",
  student:
    "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/25 dark:text-indigo-200",
  unknown: "bg-gray-100 text-gray-600 dark:bg-navy-700 dark:text-gray-300",
} as const;

export const ATTENDANCE_STATUS_SELECT_CLASS = {
  unmarked:
    "border-slate-300 bg-slate-50 text-slate-700 dark:border-slate-500/30 dark:bg-slate-500/10 dark:text-slate-200",
  checked_in:
    "border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200",
  checked_out:
    "border-sky-300 bg-sky-50 text-sky-700 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-200",
  absent:
    "border-rose-300 bg-rose-50 text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-200",
  excused_absence:
    "border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200",
  unknown:
    "border-slate-300 bg-slate-50 text-slate-700 dark:border-slate-500/30 dark:bg-slate-500/10 dark:text-slate-200",
} as const;

export const getAttendanceStatusSelectClass = (
  status?: string | null
): string => {
  if (!status) return ATTENDANCE_STATUS_SELECT_CLASS.unmarked;
  const normalized =
    status.toLowerCase() as keyof typeof ATTENDANCE_STATUS_SELECT_CLASS;
  return (
    ATTENDANCE_STATUS_SELECT_CLASS[normalized] ??
    ATTENDANCE_STATUS_SELECT_CLASS.unknown
  );
};

// Callers pass the already-translated label, so match both languages.
const PARTICIPANT_ROLE_LABEL_MATCHERS: Array<
  [string[], keyof typeof SESSION_PARTICIPANT_ROLE_BADGE_CLASS]
> = [
  [["quản lý", "manager"], "manager"], // i18n-ignore
  [["huấn luyện", "trainer"], "trainer"], // i18n-ignore
  [["học viên", "student"], "student"], // i18n-ignore
];

export const getSessionParticipantRoleBadgeClass = (
  roleLabel?: string | null
): string => {
  const normalized = (roleLabel ?? "").toLowerCase();
  const match = PARTICIPANT_ROLE_LABEL_MATCHERS.find(([needles]) =>
    needles.some((needle) => normalized.includes(needle))
  );
  return SESSION_PARTICIPANT_ROLE_BADGE_CLASS[match ? match[1] : "unknown"];
};

export const getAttendanceStatusBadgeClass = (
  status?: string | null
): string => {
  if (!status) return ATTENDANCE_STATUS_BADGE_CLASS.unmarked;
  const normalized =
    status.toLowerCase() as keyof typeof ATTENDANCE_STATUS_BADGE_CLASS;
  return (
    ATTENDANCE_STATUS_BADGE_CLASS[normalized] ??
    ATTENDANCE_STATUS_BADGE_CLASS.unknown
  );
};

export const getSessionStatusEventColor = (status?: string | null): string => {
  if (status === "ACTIVE") return CALENDAR_EVENT_COLORS.ACTIVE;
  if (status === "COMPLETED" || status === "CLOSED") {
    return CALENDAR_EVENT_COLORS.COMPLETED;
  }
  if (status === "CANCELLED") return CALENDAR_EVENT_COLORS.CANCELLED;
  return CALENDAR_EVENT_COLORS.SCHEDULED;
};

type SessionStatusTone =
  | "info"
  | "warning"
  | "scheduled"
  | "neutral"
  | "danger";

const SESSION_STATUS_TONE = {
  SCHEDULED: "info",
  ACTIVE: "scheduled",
  COMPLETED: "neutral",
  CLOSED: "neutral",
  CANCELLED: "danger",
} as const satisfies Record<string, SessionStatusTone>;

export const getSessionStatusTone = (
  status?: string | null
): SessionStatusTone => {
  const normalized = status?.toUpperCase();
  if (normalized === "ACTIVE") return SESSION_STATUS_TONE.ACTIVE;
  if (normalized === "COMPLETED" || normalized === "CLOSED") {
    return SESSION_STATUS_TONE.COMPLETED;
  }
  if (normalized === "CANCELLED") return SESSION_STATUS_TONE.CANCELLED;
  return SESSION_STATUS_TONE.SCHEDULED;
};

export const SESSION_STATUS_BADGE_CLASS = {
  SCHEDULED:
    "bg-brand-100 text-brand-800 dark:bg-brand-500/25 dark:text-brand-100",
  ACTIVE: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-200",
  COMPLETED: "bg-gray-200 text-gray-800 dark:bg-navy-600 dark:text-gray-100",
  CLOSED: "bg-gray-200 text-gray-800 dark:bg-navy-600 dark:text-gray-100",
  CANCELLED:
    "bg-danger-100 text-danger-800 dark:bg-danger-900/35 dark:text-danger-100",
} as const;

export const getSessionStatusBadgeClass = (status?: string | null): string => {
  const normalized = status?.toUpperCase();
  if (normalized === "ACTIVE") return SESSION_STATUS_BADGE_CLASS.ACTIVE;
  if (normalized === "COMPLETED" || normalized === "CLOSED") {
    return SESSION_STATUS_BADGE_CLASS.COMPLETED;
  }
  if (normalized === "CANCELLED") return SESSION_STATUS_BADGE_CLASS.CANCELLED;
  return SESSION_STATUS_BADGE_CLASS.SCHEDULED;
};
